/**
 * Consent-Gate: Blockiert die App-Nutzung, solange der eingeloggte Nutzer
 * offenen Rechtsdokument-Versionen (AGB, Datenschutz, …) noch nicht
 * zugestimmt hat. Nicht wegklickbar — Zustimmung ist Pflicht, das Dokument
 * kann vorher eingesehen/heruntergeladen werden.
 */
import { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, Dialog, DialogContent, DialogTitle, List, ListItem,
    ListItemText, Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckIcon from '@mui/icons-material/Check';
import tokenService from '../services/tokenService';
import {
    PendingConsent, consentToVersion, downloadDocumentVersion,
    getPendingConsents,
} from '../services/consentService';

const ConsentGate = () => {
    const [pending, setPending] = useState<PendingConsent[]>([]);
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        if (!tokenService.getToken()) {
            return;
        }
        try {
            setPending(await getPendingConsents());
        } catch {
            // Kein Blocker bei Netz-/Serverfehlern: das Gate prueft beim
            // naechsten App-Start erneut; die App bleibt nutzbar.
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const handleConsent = async (item: PendingConsent) => {
        setBusy(item.versionUuid);
        setError(null);
        try {
            await consentToVersion(item.versionUuid);
            setPending((current) => current.filter((p) => p.versionUuid !== item.versionUuid));
        } catch (e: any) {
            setError(e.message || 'Zustimmung konnte nicht gespeichert werden — bitte erneut versuchen.');
        } finally {
            setBusy(null);
        }
    };

    if (pending.length === 0) {
        return null;
    }

    return (
        <Dialog open fullWidth maxWidth="sm" disableEscapeKeyDown>
            <DialogTitle>Aktualisierte Vereinbarungen</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Die folgenden Dokumente wurden aktualisiert. Bitte lesen Sie die neue
                    Fassung und stimmen Sie zu, um fortzufahren.
                </Typography>
                {error && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>}
                <List>
                    {pending.map((item) => (
                        <ListItem key={item.versionUuid} divider
                            secondaryAction={
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button size="small" startIcon={<DownloadIcon />}
                                        onClick={() => downloadDocumentVersion(item.versionUuid, item.fileName)
                                            .catch((e) => setError(e.message))}>
                                        Ansehen
                                    </Button>
                                    <Button size="small" variant="contained" startIcon={<CheckIcon />}
                                        disabled={busy === item.versionUuid}
                                        onClick={() => handleConsent(item)}>
                                        Zustimmen
                                    </Button>
                                </Box>
                            }>
                            <ListItemText
                                primary={item.documentTitle}
                                secondary={`Version ${item.version} vom ${new Date(item.publishedAt).toLocaleDateString('de-DE')}`}
                            />
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
        </Dialog>
    );
};

export default ConsentGate;
